/**
 * JWT decode helpers for UI gating only — backend remains the source of truth for authorization.
 * The token's `roles` claim is set by JwtTokenProvider; `organizationId` is set when the user
 * has called /api/auth/select-organization.
 */

export type Role = 'ROLE_ADMIN' | 'ROLE_DOCTOR' | 'ROLE_CLINIC_HEAD' | string;

interface JwtPayload {
    sub?: string;
    roles?: string[];
    organizationId?: number;
    exp?: number;
}

function decodeBase64Url(input: string): string {
    const padded = input.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    try {
        return decodeURIComponent(
            atob(padded + pad)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
    } catch {
        return atob(padded + pad);
    }
}

export function getToken(): string | null {
    return localStorage.getItem('token');
}

export function decodeToken(): JwtPayload | null {
    const token = getToken();
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    try {
        return JSON.parse(decodeBase64Url(parts[1])) as JwtPayload;
    } catch {
        return null;
    }
}

export function getUsername(): string | null {
    return decodeToken()?.sub ?? null;
}

export function getRoles(): Role[] {
    const payload = decodeToken();
    return payload?.roles ?? [];
}

export function hasRole(role: Role): boolean {
    return getRoles().includes(role);
}

export function isAdmin(): boolean {
    return hasRole('ROLE_ADMIN');
}

export function isClinicHead(): boolean {
    return hasRole('ROLE_CLINIC_HEAD');
}

export function getOrganizationIdFromToken(): number | null {
    return decodeToken()?.organizationId ?? null;
}

export function isTokenExpired(): boolean {
    const payload = decodeToken();
    if (!payload?.exp) return false;
    return payload.exp * 1000 < Date.now();
}
